/// <reference types="react-dom" />
import * as React from "react";
import {  getId } from "OfficeFabric/Utilities";
import { GitPullRequestMergeStrategy } from "TFS/VersionControl/Contracts";
import * as VCResources from "VersionControl/Scripts/Resources/TFS.Resources.VersionControl";
import "VSS/LoaderPlugins/Css!VersionControl/CompletionAnimations";

export interface IMergeAnimationProps {
    mergeStrategy: GitPullRequestMergeStrategy;
    sequence?: number;
}

/* The reason this is a pure component is a bit odd. The animation on the root <svg> element gets
   out-of-sync with the animations on the lines and commits. The only way to avoid this is by providing
   a new `key` attribute on the <svg> node every time the completion type changes. That way React is
   forced to destroy the existing DOM element and create a new one.

    However, if we just do this every time the dialog is rendered, the animation restarts after every
    click, keypress, etc. That's very distracting. PureComponent implements a shallow shouldComponentUpdate()
    logic which is perfect here. It creates a new DOM element only when props.mergeStrategy changes (we
    need to switch animations) or sequence changes (we want to restart existing animation).
*/
export class CompletionAnimation extends React.PureComponent<IMergeAnimationProps> {
    public render(): JSX.Element {
        const svgProps = {
            className: "vc-completion-animation",
            key: getId("CompletionAnimation"),
            role: "img",
        };

        switch (this.props.mergeStrategy) {
            case GitPullRequestMergeStrategy.NoFastForward:
                return this._mergeAnimation(svgProps);

            case GitPullRequestMergeStrategy.Squash:
                return this._squashAnimation(svgProps);

            case GitPullRequestMergeStrategy.Rebase:
                return this._rebaseAnimation(svgProps);

            case GitPullRequestMergeStrategy.RebaseMerge:
                return this._rebaseMergeAnimation(svgProps);

            default:
                return null;
        }
    }

    private _mergeAnimation(props: React.SVGAttributes<SVGElement>): JSX.Element {
        return (
            <svg
                {...props}
                viewBox="0 0 140 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-labelledby="mergeAnimDesc"
            >
                <desc id="mergeAnimDesc">{VCResources.MergeAnimation_Description_Merge}</desc>
                <rect width="8" height="8" rx="4" className="targetCommit" />
                <rect x="44" width="8" height="8" rx="4" className="targetCommit" />
                <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommit" />
                <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommit" />
                <rect x="131" y="1" width="6" height="6" rx="4" className="mergeCommit fade-in-merge" />
                <line x1="10" y1="4" x2="42" y2="4" className="targetLine" />
                <path d="M54 4H130" className="targetLine grow-right-merge" />
                <line x1="54" y1="20" x2="86" y2="20" className="sourceLine" />
                <path d="M10 4C26 4 26 20 42 20" className="sourceLine" />
                <path d="M98 20C114 20 114 4 130 4" className="sourceLine grow-right-merge" />
            </svg>
        );
    }

    private _squashAnimation(props: React.SVGAttributes<SVGElement>): JSX.Element {
        return (
            <svg {...props}
                viewBox="0 0 96 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-labelledby="squashAnimDesc"
            >
                <desc id="squashAnimDesc">{VCResources.MergeAnimation_Description_Squash}</desc>
                <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
                <line x1="54" y1="20" x2="86" y2="20" className="sourceLineGhost fade-in-final" />
                <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
                <path d="M10 4C26 4 26 20 42 20" className="sourceLineGhost fade-in-final" />
                <rect width="8" height="8" rx="4" className="targetCommit" />
                <rect x="44" width="8" height="8" rx="4" className="targetCommit" />
                <line x1="10" y1="4" x2="42" y2="4" className="targetLine" />
                <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-squash" />
                <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommit fade-out-squash2" />
                <line x1="54" y1="20" x2="86" y2="20" className="sourceLine shrink-right-squash" />
                <path d="M54 4H86" className="sourceLine grow-right-squash" />
                <path d="M10 4C26 4 26 20 42 20" className="sourceLine fade-out-squash1" />
            </svg>
        );
    }

    private _rebaseAnimation(props: React.SVGAttributes<SVGElement>): JSX.Element {
        return (
            <svg {...props}
                viewBox="0 0 140 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-labelledby="rebaseAnimDesc"
            >
                <desc id="rebaseAnimDesc">{VCResources.MergeAnimation_Description_Rebase}</desc>
                <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
                <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
                <line x1="54" y1="20" x2="86" y2="20" className="sourceLineGhost fade-in-final" />
                <path d="M10 4C26 4 26 20 42 20" className="sourceLineGhost fade-in-final" />
                <line x1="10" y1="4" x2="42" y2="4" className="targetLine" />
                <rect width="8" height="8" rx="4" className="targetCommit" />
                <rect x="44" width="8" height="8" rx="4" className="targetCommit" />
                <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-rebase" />
                <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-rebase" />
                <line x1="54" y1="20" x2="86" y2="20" className="sourceLine slide-right-rebase" />
                <path d="M10 4C26 4 26 20 42 20" className="sourceLine fade-out-rebase" />
                <line x1="54" y1="4" x2="86" y2="4" className="sourceLine fade-in-rebase" />
            </svg>
        );
    }

    private _rebaseMergeAnimation(props: React.SVGAttributes<SVGElement>): JSX.Element {
        return (
            <svg {...props}
                viewBox="0 0 228 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-labelledby="rebaseMergeAnimDesc"
            >
                <desc id="rebaseMergeAnimDesc">{VCResources.MergeAnimation_Description_RebaseMerge}</desc>
                <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
                <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommitGhost fade-in-final" />
                <line x1="54" y1="20" x2="86" y2="20" className="sourceLineGhost fade-in-final" />
                <path d="M10 4C26 4 26 20 42 20" className="sourceLineGhost fade-in-final" />
                <line x1="10" y1="4" x2="42" y2="4" className="targetLine" />
                <line x1="54" y1="4" x2="86" y2="4" className="targetLine" />
                <line x1="98" y1="4" x2="218" y2="4" className="targetLine grow-right-rebaseMerge" />
                <rect width="8" height="8" rx="4" className="targetCommit" />
                <rect x="44" width="8" height="8" rx="4" className="targetCommit" />
                <rect x="88" width="8" height="8" rx="4" className="targetCommit" />
                <rect x="219" y="1" width="6" height="6" rx="4" className="mergeCommit fade-in-rebaseMerge" />
                <rect x="44" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-rebaseMerge" />
                <rect x="88" y="16" width="8" height="8" rx="4" className="sourceCommit slide-right-rebaseMerge" />
                <line x1="54" y1="20" x2="86" y2="20" className="sourceLine slide-right-rebaseMerge" />
                <path d="M10 4C26 4 26 20 42 20" className="sourceLine slide-right-rebaseMerge" />
                <path d="M142 20C158 20 158 4 174 4" className="sourceLine grow-right-rebaseMerge" />
            </svg>
        );
    }
}
